/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useContext, useEffect, useRef, useState } from 'react';

import { ConfigContext } from './context/ConfigContext';
import TableCellNodes from './nodes/TableCellNodes';
import AlertPlugin from './plugins/AlertPlugin';
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import EmojisPlugin from './plugins/EmojisPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import { MaxLengthPlugin } from './plugins/MaxLengthPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import TableCellResizer from './plugins/TableCellResizer';
import { TablePlugin as NewTablePlugin } from './plugins/TablePlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import YouTubePlugin from './plugins/YouTubePlugin';
import { CAN_USE_DOM } from './shared/canUseDOM';
import EditorTheme from './themes/EditorTheme';
import ContentEditable from './ui/ContentEditable';
import { ErrorBoundary } from './ui/ErrorBoundary';
import Placeholder from './ui/Placeholder';

interface Props {
  mentionOptions: string[];
}

export default function Editor({ mentionOptions }: Props) {
  const { initialValue, placeholderText, characterLimit } =
    useContext(ConfigContext);

  const CELL_EDITOR_CONFIG = {
    namespace: 'AirEditor',
    nodes: [...TableCellNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: EditorTheme,
  };

  const CONTENT_EDITABLE = (
    <ContentEditable className="TableNode__contentEditable" />
  );

  const [editor] = useLexicalComposerContext();
  const scrollRef = useRef(null);

  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] =
    useState<boolean>(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  useEffect(() => {
    function updateEditorContent(value: string) {
      const editorState = editor.parseEditorState(value);
      editor.setEditorState(editorState);
    }

    if (initialValue) {
      updateEditorContent(initialValue);
    }
  }, [editor, initialValue]);

  return (
    <>
      {editor.isEditable() ? (
        <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} />
      ) : null}
      <div className={`editor-container`} ref={scrollRef}>
        {characterLimit && <MaxLengthPlugin maxLength={characterLimit} />}
        <AlertPlugin />
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        <EmojisPlugin />
        <AutoEmbedPlugin />
        <LinkPlugin />
        <AutoLinkPlugin />
        <HistoryPlugin />
        <RichTextPlugin
          contentEditable={
            <div className="editor" ref={onRef}>
              <ContentEditable />
            </div>
          }
          placeholder={<Placeholder>{placeholderText}</Placeholder>}
          ErrorBoundary={ErrorBoundary}
        />
        <CodeHighlightPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TablePlugin />
        <TableCellResizer />
        <NewTablePlugin cellEditorConfig={CELL_EDITOR_CONFIG}>
          <AutoFocusPlugin />
          <RichTextPlugin
            contentEditable={CONTENT_EDITABLE}
            placeholder={null}
            ErrorBoundary={ErrorBoundary}
          />
          <HistoryPlugin />
          <ImagesPlugin captionsEnabled={false} />
          <LinkPlugin />
        </NewTablePlugin>
        <ImagesPlugin />
        <YouTubePlugin />
        <MentionsPlugin mentionOptions={mentionOptions} />
        {floatingAnchorElem && !isSmallWidthViewport && (
          <>
            <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />

            <FloatingTextFormatToolbarPlugin
              anchorElem={floatingAnchorElem}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          </>
        )}
      </div>
    </>
  );
}
